import React, { useState, useEffect, useRef } from 'react';
import { FaPlay, FaPause } from 'react-icons/fa';
import { Button } from '@/kenya_friars_components/buttons';
import { Link } from '@/kenya_friars_components/links';
import './HeroCarousel.css';

export interface CarouselSlide {
  id: string;
  title?: string;
  subtitle?: string;
  description?: string;
  image: string;
  link?: string;
  buttonText?: string;
}

interface HeroCarouselProps {
  slides: CarouselSlide[];
  autoPlay?: boolean;
  interval?: number;
}

export const HeroCarousel: React.FC<HeroCarouselProps> = ({
  slides,
  autoPlay = true,
  interval = 5000,
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef<any>(null);

  const startTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, interval);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => {
    if (autoPlay && !isPaused && slides.length > 1) {
      startTimer();
    } else {
      stopTimer();
    }
    return () => stopTimer();
  }, [autoPlay, isPaused, interval, slides.length]);

  const togglePause = () => {
    setIsPaused(!isPaused);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const goToPrevious = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToNext = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  if (slides.length === 0) return null;

  return (
    <div className="kf-hero-carousel">
      <div className="kf-hero-carousel__container">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`kf-hero-carousel__slide ${index === currentSlide ? 'kf-hero-carousel__slide--active' : ''
              }`}
          >
            <div
              className="kf-hero-carousel__slide-bg"
              style={{ backgroundImage: `url(${slide.image})` }}
            />
            <div className="kf-hero-carousel__overlay"></div>
            <div className="container">
              <div className="kf-hero-carousel__content">
                {slide.subtitle && (
                  <div className="kf-hero-carousel__subtitle">{slide.subtitle}</div>
                )}
                {slide.title && (
                  <h2 className="kf-hero-carousel__title">{slide.title}</h2>
                )}
                {slide.description && (
                  <p className="kf-hero-carousel__description">{slide.description}</p>
                )}
                {slide.buttonText && slide.link && (
                  <div className="kf-hero-carousel__actions">
                    {slide.link.startsWith('http') ? (
                      <Button variant="gold" href={slide.link} size="lg">
                        {slide.buttonText}
                      </Button>
                    ) : (
                      <Link to={slide.link}>
                        <Button variant="gold" size="lg">
                          {slide.buttonText}
                        </Button>
                      </Link>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {slides.length > 1 && (
        <>
          <button
            className="kf-hero-carousel__nav kf-hero-carousel__nav--prev"
            onClick={goToPrevious}
            aria-label="Previous slide"
          >
            ‹
          </button>
          <button
            className="kf-hero-carousel__nav kf-hero-carousel__nav--next"
            onClick={goToNext}
            aria-label="Next slide"
          >
            ›
          </button>

          <div className="kf-hero-carousel__indicators">
            {slides.map((_, index) => (
              <button
                key={index}
                className={`kf-hero-carousel__indicator ${index === currentSlide ? 'kf-hero-carousel__indicator--active' : ''
                  }`}
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
            <button
              className="kf-hero-carousel__pause-btn"
              onClick={togglePause}
              aria-label={isPaused ? "Play" : "Pause"}
            >
              {isPaused ? <FaPlay /> : <FaPause />}
            </button>
          </div>
        </>
      )}
    </div>
  );
};
